import { useState, useEffect, useCallback, useRef } from "react";
import { useApp } from "../context/AppContext";

// Semantics board — a mirror of the Dev-board for the semantics team.
// Membership is by task `status` (sem_*), fed from the project's "Семантика (Sem)" column.
const SEM_COLUMNS = [
  { status: "sem_take",   title: "Взяти в роботу", color: "#0ea5e9" },
  { status: "sem_work",   title: "В роботі",       color: "#3b82f6" },
  { status: "sem_review", title: "Перевірка",      color: "#06b6d4" },
  { status: "sem_done",   title: "Здано",          color: "#22c55e" },
];

// Стадійна кнопка на картці залежно від колонки (перетягування теж працює завжди).
const STAGE_ACTION = {
  sem_take:   { label: "Взяти",       to: "sem_work",   color: "#10b981", assign: true },
  sem_work:   { label: "На перевірку", to: "sem_review", color: "#06b6d4" },
  sem_review: { label: "Здати",       to: "sem_done",   color: "#22c55e" },
};

const PRIORITY_MAP = {
  low: { label: "Low", color: "#6b7280", bg: "rgba(107,114,128,0.1)" },
  medium: { label: "Medium", color: "#f59e0b", bg: "rgba(245,158,11,0.1)" },
  high: { label: "High", color: "#ef4444", bg: "rgba(239,68,68,0.1)" },
  urgent: { label: "Urgent", color: "#dc2626", bg: "rgba(220,38,38,0.15)" },
};

const initials = (name) => {
  if (!name) return "?";
  const parts = name.split(/[\s@.]+/).filter(Boolean);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || name[0].toUpperCase();
};

export default function SemanticsBoardView() {
  const { apiFetch, flash, dark, t, card, inp, btnP, btnG, btnSm, currentUser, userRole, setActiveId, setView } = useApp();

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProject, setFilterProject] = useState("");
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [me, setMe] = useState(null);
  const [detailTask, setDetailTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentText, setCommentText] = useState("");
  const commentsEndRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [boardR, usersR] = await Promise.all([
        apiFetch("/api/kanban/sem-board"),
        apiFetch("/api/kanban/users").catch(() => null),
      ]);
      const d = await boardR.json();
      if (Array.isArray(d)) setTasks(d);
      if (usersR) {
        try {
          const users = await usersR.json();
          const myUser = (users || []).find(u => u.username === currentUser);
          if (myUser) setMe(myUser);
        } catch {}
      }
    } catch (e) { console.error("Sem board load:", e); }
    setLoading(false);
  }, [apiFetch, currentUser]);

  useEffect(() => { load(); }, [load]);

  const moveTo = async (taskId, newStatus) => {
    setTasks(prev => prev.map(tk => tk.id === taskId ? { ...tk, status: newStatus } : tk));
    try {
      const r = await apiFetch(`/api/kanban/tasks/${taskId}/sem-status`, {
        method: "PATCH",
        body: JSON.stringify({ status: newStatus }),
      });
      if (!r.ok) { const d = await r.json(); flash("Помилка: " + (d.error || r.status)); load(); }
    } catch (e) { flash("Помилка: " + e.message); load(); }
  };

  // Стадійна кнопка: закріпити за собою (за потреби) + перевести в наступну колонку.
  const takeAndAdvance = async (tk, action) => {
    try {
      if (action.assign && !(me?.id && tk.sem_assignee_id === me.id)) {
        await apiFetch(`/api/kanban/tasks/${tk.id}/take-sem`, { method: "POST" });
      }
      await moveTo(tk.id, action.to);
      load();
    } catch (e) { flash("Помилка: " + e.message); }
  };

  const onDragStart = (e, taskId) => { setDragging(taskId); e.dataTransfer.effectAllowed = "move"; };
  const onDragEnd = () => { setDragging(null); setDragOver(null); };
  const onDragOver = (e, status) => { e.preventDefault(); setDragOver(status); };
  const onDrop = (e, status) => {
    e.preventDefault();
    if (dragging) moveTo(dragging, status);
    setDragging(null); setDragOver(null);
  };

  const openProject = (projId) => {
    if (!projId) return;
    setActiveId(projId);
    setView("kanban");
  };

  const loadComments = async (taskId) => {
    setCommentsLoading(true);
    try {
      const r = await apiFetch(`/api/kanban/tasks/${taskId}/comments`);
      const d = await r.json();
      if (Array.isArray(d)) setComments(d);
    } catch {}
    setCommentsLoading(false);
  };

  const openDetail = (task) => {
    setDetailTask(task);
    setComments([]);
    setCommentText("");
    loadComments(task.id);
  };

  const addComment = async () => {
    if (!commentText.trim() || !detailTask) return;
    try {
      const r = await apiFetch(`/api/kanban/tasks/${detailTask.id}/comments`, {
        method: "POST", body: JSON.stringify({ text: commentText.trim() }),
      });
      const c = await r.json();
      if (c.id) { setComments(prev => [...prev, c]); setCommentText(""); }
      setTimeout(() => commentsEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch {}
  };

  const deleteComment = async (id) => {
    setComments(prev => prev.filter(c => c.id !== id));
    try { await apiFetch(`/api/kanban/comments/${id}`, { method: "DELETE" }); } catch {}
  };

  const myId = me?.id;
  const projects = [...new Set(tasks.map(tk => tk.proj_id).filter(Boolean))];
  const visibleTasks = filterProject ? tasks.filter(tk => tk.proj_id === filterProject) : tasks;
  const tasksByStatus = (status) => visibleTasks.filter(tk => (tk.status || "sem_take") === status);

  return (
    <div style={{ height: "calc(100vh - 120px)", display: "flex", flexDirection: "column" }}>
      {/* ── Деталі задачі + коментарі ── */}
      {detailTask && (
        <div onClick={() => setDetailTask(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ ...card, width: 640, maxWidth: "100%", maxHeight: "88vh", overflow: "auto", padding: 22 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, flex: 1 }}>{detailTask.title}</h3>
              <button onClick={() => setDetailTask(null)} style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: t.textMuted }}>✕</button>
            </div>
            <div style={{ fontSize: 11, color: t.textMuted, display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
              <span>Проект: <strong>{detailTask.proj_name || detailTask.proj_id}</strong></span>
              {(detailTask.author_full_name || detailTask.author_name) && <span>Автор: {detailTask.author_full_name || detailTask.author_name}</span>}
              {(detailTask.assignee_full_name || detailTask.assignee_name) && <span>Виконавець: {detailTask.assignee_full_name || detailTask.assignee_name}</span>}
              {detailTask.priority && <span>Пріоритет: <strong style={{ color: (PRIORITY_MAP[detailTask.priority] || PRIORITY_MAP.medium).color }}>{(PRIORITY_MAP[detailTask.priority] || PRIORITY_MAP.medium).label}</strong></span>}
            </div>
            {detailTask.spec_url && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>Посилання на задачу</div>
                <a href={detailTask.spec_url} target="_blank" rel="noreferrer"
                  style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 13, color: "#0ea5e9", fontWeight: 600, textDecoration: "none", padding: "7px 12px", borderRadius: 6, border: "1px solid #0ea5e9" }}>
                  📎 Відкрити ТЗ
                </a>
              </div>
            )}
            <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>Опис</div>
            {detailTask.description
              ? <div className="rich-desc" style={{ fontSize: 14, color: "#1a1a2e", background: "#fff", padding: "16px 18px", borderRadius: 10, border: "1px solid rgba(0,0,0,0.1)", lineHeight: 1.6, marginBottom: 20, overflowWrap: "break-word", wordBreak: "break-word" }}
                  dangerouslySetInnerHTML={{ __html: detailTask.description }} />
              : <div style={{ fontSize: 14, color: t.textMuted, fontStyle: "italic", marginBottom: 20 }}>Опис не заповнено</div>}

            {/* Comments */}
            <div style={{ borderTop: `1px solid ${t.border}`, paddingTop: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>💬 Коментарі ({comments.length})</div>
              {commentsLoading && <div style={{ fontSize: 12, color: t.textMuted, padding: 10 }}>Завантаження...</div>}
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
                {comments.map(c => (
                  <div key={c.id} style={{ padding: "10px 14px", borderRadius: 10, background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)", border: `1px solid ${dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: "#0ea5e9" }}>{c.author_full_name || c.author_name?.split("@")[0] || "—"}</span>
                      <span style={{ fontSize: 10, color: t.textMuted }}>{new Date(c.created_at).toLocaleString("uk-UA")}</span>
                      {myId && c.author_id === myId && <button onClick={() => deleteComment(c.id)} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", fontSize: 11, color: dark ? "#555" : "#ccc" }}>✕</button>}
                    </div>
                    <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{c.text}</div>
                  </div>
                ))}
                {!commentsLoading && comments.length === 0 && <div style={{ fontSize: 12, color: t.textMuted, fontStyle: "italic" }}>Коментарів ще немає</div>}
                <div ref={commentsEndRef} />
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <textarea value={commentText} onChange={e => setCommentText(e.target.value)}
                  placeholder="Написати коментар..."
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); addComment(); } }}
                  rows={2} style={{ ...inp, flex: 1, fontSize: 12, padding: "8px 10px", resize: "none" }} />
                <button onClick={addComment} disabled={!commentText.trim()} style={{ ...btnP, fontSize: 11, padding: "8px 14px", alignSelf: "flex-end", opacity: commentText.trim() ? 1 : 0.5 }}>Надіслати</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, flexShrink: 0, flexWrap: "wrap" }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>📚 Збір семантики · усі проекти</h2>
        <span style={{ fontSize: 11, color: t.textMuted }}>{visibleTasks.length} з {tasks.length} задач</span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
          {projects.length > 0 && (
            <select value={filterProject} onChange={e => setFilterProject(e.target.value)} style={{ background: t.bgCard, color: t.text, border: `1px solid ${t.border}`, borderRadius: 6, padding: "4px 10px", fontSize: 11, fontFamily: "inherit" }}>
              <option value="">Усі проекти</option>
              {projects.map(pid => {
                const sample = tasks.find(tk => tk.proj_id === pid);
                return <option key={pid} value={pid}>{sample?.proj_name || pid}</option>;
              })}
            </select>
          )}
          <button onClick={load} style={{ ...btnG, fontSize: 11 }}>↻ Оновити</button>
        </div>
      </div>

      {loading && <div style={{ textAlign: "center", padding: 40, color: t.textMuted }}>Завантаження...</div>}

      {!loading && tasks.length === 0 && (
        <div style={{ ...card, padding: 40, textAlign: "center", color: t.textMuted }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Дошка семантики пуста</div>
          <div style={{ fontSize: 12 }}>Задачі з'являться тут після того, як SEO-фахівець перенесе задачу в колонку «Семантика (Sem)» на канбані проекту.</div>
        </div>
      )}

      {!loading && tasks.length > 0 && (
        <div style={{ display: "flex", gap: 12, flex: 1, overflowX: "auto", paddingBottom: 8 }}>
          {SEM_COLUMNS.map(col => {
            const colTasks = tasksByStatus(col.status);
            const isOver = dragOver === col.status;
            return (
              <div
                key={col.status}
                onDragOver={(e) => onDragOver(e, col.status)}
                onDrop={(e) => onDrop(e, col.status)}
                onDragLeave={() => setDragOver(null)}
                style={{
                  minWidth: 260, flex: "1 1 260px", maxWidth: 340,
                  background: t.bgCard, border: `1px solid ${isOver ? col.color : t.border}`,
                  borderRadius: 10, padding: 10,
                  display: "flex", flexDirection: "column",
                  outline: isOver ? `2px dashed ${col.color}` : "none",
                  transition: "border-color .15s",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, paddingBottom: 6, borderBottom: `2px solid ${col.color}` }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: t.text, lineHeight: 1.2, flex: 1 }}>{col.title}</span>
                  <span style={{ fontSize: 10, color: t.textMuted, fontWeight: 700, background: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)", padding: "2px 7px", borderRadius: 10 }}>{colTasks.length}</span>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 6, overflowY: "auto", flex: 1 }}>
                  {colTasks.length === 0 && <div style={{ fontSize: 10.5, color: t.textMuted, padding: "10px 4px", textAlign: "center", opacity: 0.5 }}>—</div>}
                  {colTasks.map(tk => {
                    const pr = PRIORITY_MAP[tk.priority] || PRIORITY_MAP.medium;
                    const semName = tk.sem_assignee_full_name || (tk.sem_assignee_name ? tk.sem_assignee_name.split("@")[0] : null);
                    const takenByMe = myId && tk.sem_assignee_id === myId;

                    return (
                      <div
                        key={tk.id}
                        draggable
                        onDragStart={(e) => onDragStart(e, tk.id)}
                        onDragEnd={onDragEnd}
                        onClick={() => openDetail(tk)}
                        title="Відкрити задачу"
                        style={{
                          padding: "9px 10px",
                          borderRadius: 7,
                          background: dark ? "rgba(255,255,255,0.025)" : "rgba(0,0,0,0.02)",
                          border: `1px solid ${t.border}`,
                          borderLeft: `3px solid ${col.color}`,
                          cursor: "grab",
                          opacity: dragging === tk.id ? 0.5 : 1,
                        }}
                      >
                        {/* Top: project badge + tags */}
                        <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 5, flexWrap: "wrap" }}>
                          <span
                            onClick={(e) => { e.stopPropagation(); openProject(tk.proj_id); }}
                            title="Відкрити проект"
                            style={{ fontSize: 9, fontWeight: 700, color: "#0ea5e9", padding: "1px 6px", borderRadius: 3, background: dark ? "rgba(14,165,233,0.12)" : "rgba(14,165,233,0.08)", cursor: "pointer", textTransform: "uppercase", letterSpacing: 0.3, maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          >
                            {tk.proj_name || tk.proj_id || "—"}
                          </span>
                          <span style={{ fontSize: 8, fontWeight: 700, padding: "1px 5px", borderRadius: 3, background: pr.bg, color: pr.color, textTransform: "uppercase" }}>{pr.label}</span>
                        </div>

                        {/* Title */}
                        <div style={{ fontSize: 12, fontWeight: 600, color: t.text, marginBottom: 4, lineHeight: 1.3 }}>{tk.title}</div>

                        {/* Description preview */}
                        {tk.description && <div style={{ fontSize: 10.5, color: t.textMuted, marginBottom: 6, maxHeight: 32, overflow: "hidden", lineHeight: 1.3 }}>{tk.description.replace(/<[^>]*>/g, "").slice(0, 100)}{tk.description.length > 100 ? "…" : ""}</div>}

                        {/* Bottom: sem-assignee + take button */}
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 6, gap: 6 }}>
                          {semName ? (
                            <span title={`Семантист: ${semName}`}
                              style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, color: takenByMe ? "#10b981" : t.text, fontWeight: 600 }}>
                              <span style={{ width: 18, height: 18, borderRadius: "50%", background: takenByMe ? "#10b981" : "#0ea5e9", color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700 }}>{initials(semName)}</span>
                              <span style={{ maxWidth: 80, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{semName}</span>
                            </span>
                          ) : (
                            <span style={{ fontSize: 10, color: t.textMuted, fontStyle: "italic" }}>не взято</span>
                          )}
                          {STAGE_ACTION[col.status] && (
                            <button
                              onClick={(e) => { e.stopPropagation(); takeAndAdvance(tk, STAGE_ACTION[col.status]); }}
                              title={STAGE_ACTION[col.status].label}
                              style={{
                                border: `1px solid ${STAGE_ACTION[col.status].color}`,
                                background: STAGE_ACTION[col.status].color,
                                color: "#fff",
                                fontSize: 9, fontWeight: 700,
                                padding: "3px 9px", borderRadius: 4, cursor: "pointer",
                                fontFamily: "inherit", whiteSpace: "nowrap",
                              }}
                            >
                              {STAGE_ACTION[col.status].label}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
